import { Empty, ErrorState, Loading, PageHead, TraceChip, useFormat } from '@ly360/ui';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';

interface AuditEntry {
  seq: string;
  occurredAt: string;
  actorType: string;
  actorName: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  outcome: 'success' | 'denied' | 'error';
  traceId: string | null;
}

const OUTCOME_CLASS = { success: 'badge-success', denied: 'badge-warning', error: 'badge-danger' } as const;

export function AuditPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const q = useInfiniteQuery({
    queryKey: ['audit'],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api<{ items: AuditEntry[]; nextBeforeSeq: string | null }>(
        `/v1/audit?limit=50${pageParam ? `&beforeSeq=${pageParam}` : ''}`,
      ),
    getNextPageParam: (last) => last.nextBeforeSeq,
  });
  const verify = useMutation({
    mutationFn: () =>
      api<{ intact: boolean; checked: number; firstBadSeq: string | null }>('/v1/audit/verify'),
  });
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <PageHead
        eyebrow={t('nav.admin')}
        title={t('audit.title')}
        subtitle={t('audit.subtitle')}
        actions={
          <button
            className="btn btn-grad"
            onClick={() => verify.mutate()}
            disabled={verify.isPending}
            data-testid="verify-audit"
          >
            <ShieldCheck size={16} />
            {verify.isPending ? t('audit.verifying') : t('audit.verify')}
          </button>
        }
      />
      {verify.data && (
        <div
          className={`card card-pad`}
          role="status"
          data-testid="verify-result"
          style={{ marginBottom: '1rem', display: 'flex', gap: 10, alignItems: 'center' }}
        >
          {verify.data.intact ? (
            <ShieldCheck color="var(--success)" />
          ) : (
            <ShieldAlert color="var(--danger)" />
          )}
          <strong>
            {verify.data.intact
              ? t('audit.intact', {
                  count: verify.data.checked,
                  formattedCount: fmt.number(verify.data.checked),
                })
              : t('audit.broken', { seq: verify.data.firstBadSeq })}
          </strong>
        </div>
      )}
      <section className="card">
        {q.isLoading && <Loading />}
        {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
        {q.isSuccess && rows.length === 0 && <Empty text={t('audit.empty')} />}
        {rows.length > 0 && (
          <div className="table-wrap">
            <table className="table table-responsive">
              <thead>
                <tr>
                  <th>{t('audit.col.seq')}</th>
                  <th>
                    {t('audit.col.time')} <span className="faint">({fmt.zoneCaption})</span>
                  </th>
                  <th>{t('audit.col.actor')}</th>
                  <th>{t('audit.col.action')}</th>
                  <th>{t('audit.col.target')}</th>
                  <th>{t('audit.col.outcome')}</th>
                  <th>{t('audit.col.trace')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.seq}>
                    <td className="num" data-label={t('audit.col.seq')}>
                      {fmt.number(Number(r.seq))}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }} data-label={t('audit.col.time')}>
                      {fmt.dateTime(r.occurredAt)}
                    </td>
                    <td dir="auto" data-label={t('audit.col.actor')}>
                      {r.actorName ?? t(`audit.actorType.${r.actorType}`)}
                    </td>
                    <td data-label={t('audit.col.action')}>
                      <code className="mono" style={{ fontSize: 'var(--t-xs)' }}>
                        {r.action}
                      </code>
                    </td>
                    <td
                      className="faint mono"
                      style={{ fontSize: 'var(--t-xs)' }}
                      data-label={t('audit.col.target')}
                    >
                      {r.targetType ? `${r.targetType}:${r.targetId?.slice(0, 8)}` : '—'}
                    </td>
                    <td data-label={t('audit.col.outcome')}>
                      <span className={`badge ${OUTCOME_CLASS[r.outcome]}`}>
                        {t(`audit.outcome.${r.outcome}`)}
                      </span>
                    </td>
                    <td data-label={t('audit.col.trace')}>
                      <TraceChip traceId={r.traceId} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {q.hasNextPage && (
          <div className="card-pad" style={{ textAlign: 'center' }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => void q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
            >
              {t('audit.loadMore')}
            </button>
          </div>
        )}
      </section>
    </>
  );
}
